'use client'

// import { useHeaderTheme } from '@/providers/HeaderTheme'
import Link from 'next/link'
import React, { useCallback, useMemo } from 'react'
import { SearchIcon } from 'lucide-react'
import { motion } from 'motion/react'

import { Logo } from '@/components/Logo/Logo'
import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import { useHeaderNavigationContext } from '@/providers/HeaderNavigation'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

import { MenuButton } from './components/MenuButton'
import { NavCenter } from './Nav/NavCenter'
import { NavDropdown } from './Nav/NavDropdown'
import { NavMobile } from './Nav/NavMobile'
import { NavUser } from './Nav/NavUser'

import type { Header as HeaderType } from '@/payload-types'

export const HeaderClient: React.FC<{
  data: HeaderType
}> = ({ data }) => {
  const leftNav = data?.leftNav as HeaderType['leftNav'] | undefined
  const centerNav = data?.centerNav as HeaderType['centerNav'] | undefined
  const dropdown = data?.dropdown as HeaderType['dropdown'] | undefined

  const { isMobile, hasScrolled } = useLenisContext()
  const { isDropdownOpen, setIsDropdownOpen, isMenuOpen, setIsMenuOpen, colorInverse } =
    useHeaderNavigationContext()

  const headerClasses = useMemo(() => {
    return cn(
      'group fixed top-2 left-0 z-50 w-full border-none bg-transparent transition-colors duration-500 max-lg:flex max-lg:flex-col',
      // only clip overflow when menu is closed so the fixed mobile menu isn't clipped when open
      !isMenuOpen && 'max-lg:overflow-hidden',
    )
  }, [isMenuOpen])

  const BAR_CLASSES = useMemo(() => {
    return cn(
      'pointer-events-none absolute inset-x-0 top-0 z-50 wrapper h-15 shrink-0 rounded-2xl border border-border/50 bg-background/50 transition-all duration-500 max-wrapper:inset-x-2 max-wrapper:w-[calc(100%-16px)]',
      hasScrolled && 'backdrop-blur-[1em]',
      // colorInverse && 'bg-foreground/30 border-border/31',
      isDropdownOpen && 'border-border/86 bg-background/85',
      colorInverse && 'border-border/51 bg-foreground/50',
      colorInverse && isDropdownOpen && 'border-border/51 bg-foreground/50',
    )
  }, [colorInverse, isDropdownOpen, hasScrolled])

  const LINK_CLASSES = useMemo(() => {
    return cn(
      'h-10 px-3.5 text-reg! font-medium text-neutral-900 opacity-100 hover:bg-accent-foreground/50 hover:text-neutral-900 hover:opacity-100',
      colorInverse &&
        'text-white hover:bg-accent/25 hover:text-white focus-visible:ring-evergreen-300!',
    )
  }, [colorInverse])

  const handleNavbarClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const target = e.target as HTMLElement
      const closest = target.closest('a, button') as HTMLElement | null

      // Don’t close when clicking the dropdown/mobile menu toggle button
      if (
        target.getAttribute('aria-controls') === 'nav-dropdown' ||
        closest?.getAttribute('aria-controls') === 'nav-dropdown' ||
        target.getAttribute('aria-controls') === 'nav-mobile' ||
        closest?.getAttribute('aria-controls') === 'nav-mobile'
      ) {
        return
      }

      // Close dropdown when a link or button is clicked
      if (
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        closest?.tagName === 'A' ||
        closest?.tagName === 'BUTTON'
      ) {
        setIsDropdownOpen(false)
        setIsMenuOpen(false)
      }
    },
    [setIsDropdownOpen, setIsMenuOpen],
  )

  return (
    <>
      <motion.header
        className={headerClasses}
        data-theme="light"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      >
        {/* Bar background only — z-50 so menu (z-51) and nav (z-52) can sit above */}
        <div className={BAR_CLASSES} aria-hidden />

        {/* Nav row — same layout, z-52 so it stays above the slide menu */}
        <nav
          aria-label="Main navigation"
          className={cn(
            'relative z-52 wrapper flex h-15 shrink-0 items-center justify-between pr-2 pl-4 max-wrapper:w-[calc(100%-16px)] lg:grid lg:grid-cols-[minmax(auto,1fr)_auto_minmax(auto,1fr)] lg:grid-rows-1 lg:gap-3',
          )}
          onClick={handleNavbarClick}
        >
          {/* Left nav */}
          <div className="col-start-1 col-end-2 row-start-1 row-end-2 flex items-center">
            <Link
              href="/"
              className={cn(
                'cursor-pointer rounded-lg py-1 ring-offset-transparent focus-visible:ring focus-visible:ring-evergreen-500 focus-visible:ring-offset-2 focus-visible:outline-none',
                colorInverse && 'focus-visible:ring-evergreen-300!',
              )}
            >
              {leftNav?.logo ? (
                <Media
                  resource={leftNav.logo}
                  className="h-8 w-auto object-cover object-center"
                  pictureClassName="h-8 w-auto object-cover object-center"
                  imgClassName={cn(
                    'h-8 w-auto object-cover object-center',
                    colorInverse && !isMenuOpen && 'invert-100',
                  )}
                />
              ) : (
                <Logo className="h-8 w-auto" />
              )}
            </Link>
          </div>

          {/* Center nav */}
          {centerNav && <NavCenter data={centerNav} />}

          {/* Dropdown menu (desktop only) */}
          {dropdown && <NavDropdown data={data} />}

          {/* Right nav */}
          <div className="flex h-full items-center justify-end lg:col-start-3 lg:col-end-4 lg:row-start-1 lg:row-end-2">
            <div className="flex h-full items-center gap-0">
              <Button
                variant="link"
                icon={false}
                asChild
                className={cn(
                  LINK_CLASSES,
                  'size-10! shrink-0 justify-center p-0 lg:hidden',
                  isMenuOpen && 'text-neutral-900',
                  colorInverse && 'focus-visible:ring-evergreen-300!',
                )}
              >
                <Link href="/search">
                  <span className="sr-only">Search</span>
                  <SearchIcon className="size-5" />
                </Link>
              </Button>
              <NavUser
                colorInverse={colorInverse}
                isMenuOpen={isMenuOpen}
                LINK_CLASSES={LINK_CLASSES}
              />
              <Button
                variant={colorInverse ? 'primary' : 'accent'}
                color={colorInverse ? 'inverse' : 'default'}
                icon={false}
                asChild
                className={cn(
                  'ml-1 hidden lg:inline-flex',
                  colorInverse && 'focus-visible:ring-evergreen-300!',
                )}
              >
                <Link href="/donate">Donate</Link>
              </Button>
              <MenuButton />
            </div>
          </div>
        </nav>

        {/* Mobile slideout menu — inside header so it stacks above wrapper (z-50) but below nav buttons (z-52) */}
        {isMobile && (
          <motion.nav
            initial={false}
            animate={
              isMenuOpen ? { x: 0, pointerEvents: 'auto' } : { x: '100%', pointerEvents: 'none' }
            }
            transition={{
              duration: 0.3,
              ease: 'easeOut',
            }}
            className="fixed top-0 left-0 z-51 flex h-svh min-h-0 w-full flex-col overflow-hidden bg-background pt-15"
            id="nav-mobile"
            aria-hidden={!isMenuOpen}
            role="menu"
            onClick={handleNavbarClick}
          >
            <NavMobile data={data} />
          </motion.nav>
        )}
      </motion.header>

      {/* Dropdown blur */}
      <motion.div
        className="nav-dropdown-blur fixed top-0 left-0 z-49 size-full bg-black/5 backdrop-blur-xs max-lg:hidden"
        initial={false}
        animate={
          isDropdownOpen
            ? { opacity: 1, visibility: 'visible' }
            : { opacity: 0, visibility: 'hidden' }
        }
        transition={{
          duration: 0.4,
          delay: isDropdownOpen ? 0 : 0.2,
          ease: 'easeOut',
        }}
        onClick={() => setIsDropdownOpen(false)}
      />
    </>
  )
}
